import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  Bot, 
  Package, 
  Clock 
} from 'lucide-react';
import { usePermissions, PERMISSIONS } from '../../hooks/usePermissions';

const BottomNavigation: React.FC = () => {
  const { hasPermission } = usePermissions();
  const location = useLocation();

  // Hide on AI Chatbot page
  if (location.pathname === '/ai-chatbot') {
    return null;
  }

  const navItems = [
    {
      path: '/dashboard',
      icon: LayoutDashboard,
      label: 'Home',
      permission: PERMISSIONS.DASHBOARD_LIMITED
    },
    {
      path: '/orders',
      icon: ShoppingCart,
      label: 'Orders',
      permission: PERMISSIONS.ORDERS_VIEW
    },
    {
      path: '/ai-chatbot',
      icon: Bot,
      label: 'AI',
      permission: PERMISSIONS.DASHBOARD_LIMITED
    },
    {
      path: '/inventory',
      icon: Package,
      label: 'Inventory',
      permission: PERMISSIONS.INVENTORY_VIEW
    },
    {
      path: '/order-history',
      icon: Clock,
      label: 'History',
      permission: PERMISSIONS.HISTORY_LIMITED
    }
  ];

  // Filter items by current user permissions
  const allowedNavItems = navItems.filter(item => hasPermission(item.permission));

  // If the user has no allowed navigation items, don't render the bottom bar
  if (allowedNavItems.length === 0) {
    return null;
  }

  return (
    <div className="tablet:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/85 dark:bg-gray-900/85 backdrop-blur-lg border-t border-gray-200/50 dark:border-gray-800/50 shadow-[0_-4px_24px_rgba(0,0,0,0.04)] dark:shadow-[0_-4px_24px_rgba(0,0,0,0.15)] pb-[env(safe-area-inset-bottom,0px)] transition-all duration-300">
      <div className="flex items-center justify-around h-16 px-2 max-w-md mx-auto">
        {allowedNavItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className="flex-1 flex justify-center"
            >
              {({ isActive }) => (
                <div className={`group flex flex-col items-center justify-center w-14 h-14 rounded-xl transition-all duration-300 relative overflow-hidden active:scale-90
                  ${isActive 
                    ? 'text-primary-600 dark:text-primary-400' 
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }
                `}>
                  {/* Subtle active background glow */}
                  <span className={`absolute inset-1 rounded-lg bg-primary-100/40 dark:bg-primary-900/10 transition-all duration-500 ease-out scale-75 opacity-0
                    ${isActive ? 'scale-100 opacity-100' : 'group-hover:scale-95 group-hover:opacity-50'}
                  `} />
                  
                  {/* Mini-line indicator at top */}
                  <span className={`absolute top-0 w-5 h-[2px] bg-primary-600 dark:bg-primary-400 rounded-full transition-all duration-300
                    ${isActive ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}
                  `} />

                  <Icon className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 z-10
                    ${isActive ? 'scale-110 -translate-y-[2px] rotate-3' : 'group-hover:scale-110 group-hover:rotate-6'}
                  `} />

                  <span className={`text-[10px] font-semibold mt-1 tracking-wide z-10 transition-all duration-300
                    ${isActive ? 'font-bold translate-y-[-1px]' : 'opacity-85'}
                  `}>
                    {item.label}
                  </span>
                </div>
              )}
            </NavLink>
          );
        })}
      </div>
    </div>
  );
};

export default BottomNavigation;
